<template>
  <div class="images-mosaic list-container" ref="additionalTips"
    :class="{ 
      'hide-num': isShowNum === 'false', 
      'rtlEnable': !isRTLEnable === false,
      'no-content-mode': !isContentAvailableBoolean
    }">
    <VueSlickCarousel v-if="!isRTLEnable" v-bind="computedSettings" :key="'ltr-' + refreshKey">
      <slot />
    </VueSlickCarousel>
    <VueSlickCarouselRtl v-else :rtl="true" v-bind="computedSettings" :key="'rtl-' + refreshKey">
      <slot />
    </VueSlickCarouselRtl>
  </div>
</template>
<script>

import VueSlickCarousel from 'vue-slick-carousel';
import VueSlickCarouselRtl from 'vue-slick-carousel-rtl';
export default {
  name: 'images-mosaic-additional-tips',
  components: { VueSlickCarousel, VueSlickCarouselRtl },
  props: {
    isShowNum: {
      type: String,
      default: "true"
    },
    isContentAvailable: {
      type: String,  
      default: "true"
    },
    navTaggingLabel: { type: [String, Array], required: false, default: '' },
    pageCategory: { type: String, required: false, default: 'page category' },
  },

  data() {
    // Constants for carousel calculations
    const CAROUSEL_CONSTANTS = {
      SLIDE_WIDTH: 232,
      GAP: 24,
      LEFT_PADDING: 72,
      MOBILE_BREAKPOINT: 768,
    };

    // Common settings shared across breakpoints
    const commonSettings = {
      speed: 500,
      initialSlide: 0,
      touchThreshold: 5,
      lazyLoad: 'progressive',
    };

    return {
      refreshKey: 0,
      isRTLEnable: false,
      constants: CAROUSEL_CONSTANTS,
      baseSettings: {
        dots: false,
        arrows: true,
        infinite: false,
        slidesToShow: 3,
        swipe: false,
        slidesToScroll: 1,
        ...commonSettings,
        responsive: [
          {
            breakpoint: 1023,
            settings: {
              ...commonSettings,
              dots: true,
              arrows: false,
              slidesToShow: 2,
              slidesToScroll: 1,
              swipe: true,
              focusOnSelect: true,
            }
          },
          {
            breakpoint: CAROUSEL_CONSTANTS.MOBILE_BREAKPOINT,
            settings: {
              ...commonSettings,
              centerMode: true,
              centerPadding: '25px',
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              swipe: true,
              dots: true,
              focusOnSelect: true,
            }
          },
        ]
      },
    };
  },

  mounted() {
    this.checkRTLSupport();
  },
  watch: {
    isContentAvailableBoolean() {
      if (this.isMobile) {
        this.refreshKey++;
      }
    }
  },
  computed: {
    isMobile() {
      return typeof window !== 'undefined' && window.innerWidth <= this.constants.MOBILE_BREAKPOINT;
    },
    isContentAvailableBoolean() {
      // Convert string to boolean
      return this.isContentAvailable === 'true' || this.isContentAvailable === true;
    },
    noContent() {
      return !this.isContentAvailableBoolean;
    },
    computedSettings() {
      // Shallow clone to avoid deep cloning overhead
      const settings = { ...this.baseSettings, responsive: [...this.baseSettings.responsive] };

      if (this.isMobile) {
        const mobileConfig = settings.responsive.find(r => r.breakpoint === this.constants.MOBILE_BREAKPOINT);
        if (mobileConfig) {
          // Shallow clone mobile settings
          mobileConfig.settings = { ...mobileConfig.settings };
          
          if (this.noContent) {
            Object.assign(mobileConfig.settings, {
              slidesToShow: 1.2,
              centerMode: false,
              variableWidth: true,
              centerPadding: '0',
              swipeToSlide: true,
              edgeFriction: 0.15,
              useCSS: true,
              afterChange: this.handleAfterChange,
              afterInit: this.handleAfterInit,
            });
          } else {
            mobileConfig.settings.slidesToShow = 1;
            mobileConfig.settings.centerMode = true;
          }
        }
      }

      return settings;
    },
  },
  methods: {
    checkRTLSupport() {
      const wrapper = this.$refs.additionalTips;
      if (!wrapper) return;

      const parent = wrapper.parentElement;
      const isParentAdditionalTips = parent?.classList.contains('additional-tips');
      const mainContainer = document.getElementById('main-container');
      
      if (!mainContainer || !isParentAdditionalTips) {
        this.isRTLEnable = false;
        return;
      }

      const isRTL = getComputedStyle(mainContainer).direction === 'rtl';
      this.isRTLEnable = isRTL;
      
      if (isRTL) {
        parent.classList.add('additional-tips-rtl');
      }
    },
    handleAfterChange(currentSlide) {
      this.$nextTick(() => {
        this.adjustCarouselPosition(currentSlide);
      });
    },
    handleAfterInit() {
      this.$nextTick(() => {
        this.adjustCarouselPosition(0);
      });
    },
    adjustCarouselPosition(currentSlide) {
      // Early return if conditions not met
      if (!this.isMobile || this.isContentAvailableBoolean) {
        return;
      }

      const slider = this.$refs.additionalTips;
      if (!slider) return;

      const slickList = slider.querySelector('.slick-list');
      const slickTrack = slider.querySelector('.slick-track');
      if (!slickList || !slickTrack) return;

      const slides = slickTrack.querySelectorAll('.slick-slide:not(.slick-cloned)');
      if (!slides?.length) return;

      const { SLIDE_WIDTH, GAP, LEFT_PADDING } = this.constants;
      const totalSlides = slides.length;
      const viewportWidth = slickList.offsetWidth;

      // Calculate dimensions
      const totalContentWidth = (SLIDE_WIDTH * totalSlides) + (GAP * (totalSlides - 1));
      const availableViewport = viewportWidth - LEFT_PADDING;
      const maxTransform = Math.max(0, totalContentWidth - availableViewport);

      // Get and parse current transform
      const transform = getComputedStyle(slickTrack).transform;
      const currentTransformX = this.parseTransformX(transform);
      const isLastSlide = currentSlide >= totalSlides - 1;

      // Adjust position if needed
      if (isLastSlide || Math.abs(currentTransformX) > maxTransform) {
        slickTrack.style.transform = `translate3d(${-maxTransform}px, 0px, 0px)`;
      }
    },
    parseTransformX(transform) {
      if (!transform || transform === 'none') return 0;

      // Try matrix format first: matrix(a, b, c, d, tx, ty)
      const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
      if (matrixMatch) {
        const values = matrixMatch[1].split(',');
        return parseFloat(values[4]) || 0;
      }

      // Try translate3d format: translate3d(tx, ty, tz)
      const translate3dMatch = transform.match(/translate3d\(([^)]+)\)/);
      if (translate3dMatch) {
        const values = translate3dMatch[1].split(',');
        return parseFloat(values[0]) || 0;
      }

      return 0;
    }
  },
};
</script>
<style lang="scss" src="./ImagesMosaicAdditionalTips.scss"></style>
